import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    model,
    OnInit,
    signal
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ButtonComponent, TableComponent, TableColumn } from '@siemens/component-lib';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SnowflakeSearchParams, SnowflakeSearchResult } from '../../interfaces/workflows/SnowflakeSearchResult';
import { TABLE_MODE } from '../../enums/TableMode.enum';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    finalize,
    forkJoin,
    map,
    Observable,
    of,
    startWith,
    switchMap,
    tap,
    throwError
} from 'rxjs';
import { Store } from '@ngxs/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { WorkflowOptionSetService } from '../../services/workflow-option-set.service';
import { WorkflowConfigSection, WorkflowConfigStep } from '@monorepo/interfaces';
import { SnowflakeSearchTableOperations } from './snowflake-search-table.operations';
import { SnowflakeSearchState } from '../../state/snowflake/snowflake-search.state';
import { ClearSnowflakeSearch, SearchSnowflake } from '../../state/snowflake/snowflake-search.actions';
import { ErrorService } from '../../services/error.service';

@Component({
    selector: 'app-snowflake-search',
    imports: [ButtonComponent, TableComponent, TranslatePipe, LoadingIndicatorComponent],
    templateUrl: './snowflake-search.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SnowflakeSearchComponent implements OnInit {
    private store = inject(Store);
    private workflowOptionSetService = inject(WorkflowOptionSetService);
    private translateService = inject(TranslateService);
    private destroyRef = inject(DestroyRef);
    private errorService = inject(ErrorService);

    private readonly PRODUCT_LIMIT = 50;

    public readonly stepForm = input.required<FormGroup>();
    public readonly stepConfig = input.required<WorkflowConfigSection | WorkflowConfigStep>();
    public readonly disabled = input<boolean>(false);

    protected readonly snowflakeResults = this.store.selectSignal(SnowflakeSearchState.results);
    protected readonly isSnowflakeSearchLoading = this.store.selectSignal(SnowflakeSearchState.isLoading);
    protected readonly isCoreMlfbSet = signal<boolean>(false);
    protected readonly hasSearchedSnowflake = this.store.selectSignal(SnowflakeSearchState.hasSearched);
    protected readonly tableMode = TABLE_MODE.SERVER;
    protected readonly pageSizeOptions = [5, 10, 20];
    protected readonly currentPage = model<number>(1);
    protected readonly paginationPageSize = model<number>(5);
    protected readonly snowflakeTotalItems = this.store.selectSignal(SnowflakeSearchState.totalItems);
    protected readonly hasExcessiveSnowflakeResults = computed(
        () => (this.snowflakeTotalItems() ?? 0) > this.PRODUCT_LIMIT
    );
    private readonly lastSnowflakeSearchParams = this.store.selectSignal(SnowflakeSearchState.lastSearchParams);

    private readonly currentLang = toSignal(this.translateService.onLangChange.pipe(map(event => event.lang)), {
        initialValue: this.translateService.getCurrentLang()
    });
    private readonly currentPage$ = toObservable(this.currentPage);
    private readonly paginationPageSize$ = toObservable(this.paginationPageSize);

    protected readonly snowflakeColumns = computed<TableColumn<SnowflakeSearchResult>[]>(() => {
        const _lang = this.currentLang();
        return SnowflakeSearchTableOperations.getTableColumns(this.translateService);
    });

    protected readonly snowflakeTableConfig = SnowflakeSearchTableOperations.tableConfig;

    /**
     * Initializes the component and sets up all event listeners.
     */
    public ngOnInit(): void {
        this.setupPaginationListener();
        this.setupCoreMlfbListener();
        this.setupSnowflakeGckListener();
        this.setupSnowflakePCKListener();
    }

    /**
     * Sets up a listener that triggers Snowflake search when pagination changes.
     * Monitors both current page and page size changes, and re-executes the last search with new pagination parameters.
     */
    private setupPaginationListener(): void {
        combineLatest([this.currentPage$, this.paginationPageSize$])
            .pipe(
                distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1]),
                filter(() => this.hasSearchedSnowflake()),
                tap(() => {
                    const params = this.lastSnowflakeSearchParams();
                    if (params) {
                        const page = Math.max(0, this.currentPage() - 1);
                        const pageSize = this.paginationPageSize();
                        this.dispatchSnowflakeSearch(params, page, pageSize);
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    /**
     * Dispatches a Snowflake search action to the store with the provided parameters.
     * Handles errors by logging them to the console.
     * @param params - The search parameters (coreMlfb, gck, pck)
     * @param page - The zero-based page number
     * @param pageSize - The number of items per page
     */
    private dispatchSnowflakeSearch(params: SnowflakeSearchParams, page: number, pageSize: number): void {
        this.store
            .dispatch(new SearchSnowflake(params, page, pageSize))
            .pipe(
                catchError(error => {
                    this.errorService.handleError(this.translateService.instant('workflow.snowflake.search-error'));
                    console.error('Snowflake search error:', error);
                    return throwError(() => error);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    /**
     * Initiates a Snowflake search based on the form values.
     * Extracts search parameters from the form (coreMlfb, gck, pck, priceGroup) and dispatches the search action.
     * Supports both single and multiple PCK/priceGroup selections.
     * Resets pagination to the first page before searching.
     */
    protected searchSnowflake(): void {
        const form = this.stepForm();
        const coreMlfb = form.get('search-core-mlfb')?.value as string;
        const gck = form.get('gck-search')?.value as string;
        const pck = form.get('pck-search')?.value as string[];
        const priceGroupValue = form.get('price-group-search')?.value as string[];

        if (!coreMlfb) {
            this.store.dispatch(new ClearSnowflakeSearch());
            return;
        }

        // Handle price groups: map the "empty" label to empty string for backend
        const emptyPriceGroupLabel = this.translateService.instant('workflow.snowflake.empty-price-group');
        let priceGroup: string[] | undefined;
        if (Array.isArray(priceGroupValue)) {
            priceGroup = priceGroupValue.map(pg => (pg === emptyPriceGroupLabel ? '' : pg));
        } else if (priceGroupValue) {
            priceGroup = [priceGroupValue === emptyPriceGroupLabel ? '' : priceGroupValue];
        }

        const params: SnowflakeSearchParams = {
            coreMlfb,
            gck: gck || undefined,
            pck: pck && pck.length > 0 ? pck : undefined,
            priceGroup: priceGroup && priceGroup.length > 0 ? priceGroup : undefined
        };

        this.currentPage.set(1);
        const page = Math.max(0, this.currentPage() - 1);
        const pageSize = this.paginationPageSize();

        this.dispatchSnowflakeSearch(params, page, pageSize);
    }

    /**
     * Clears all search fields and results.
     * Resets the form controls, pagination, and dispatches a clear action to the store.
     */
    protected clearSnowflakeSearch(): void {
        const form = this.stepForm();
        form.get('search-core-mlfb')?.reset();
        form.get('gck-search')?.reset();
        form.get('pck-search')?.reset();
        form.get('price-group-search')?.reset();
        this.currentPage.set(1);
        this.updateSnowflakePckOptions([]);
        this.updateSnowflakePricegroupValue([]);
        this.store.dispatch(new ClearSnowflakeSearch());
    }

    /**
     * Sets up a listener for GCK changes.
     * When GCK changes, fetches available PCK options and resets dependent fields (PCK and price group).
     */
    private setupSnowflakeGckListener(): void {
        const form = this.stepForm();

        const gckControl = form.get('gck-search') as FormControl;
        const pckControl = form.get('pck-search') as FormControl;

        if (!gckControl || !pckControl) return;

        gckControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                switchMap(value => {
                    if (!value) {
                        this.updateSnowflakePckOptions([]);
                        pckControl.reset();
                        return of([''] as string[]);
                    }
                    this.updateSnowflakePricegroupValue([]);
                    return this.loadPckOptionsForGck(value.toString());
                }),
                tap(options => {
                    this.updateSnowflakePckOptions(options ?? []);
                    pckControl.reset();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    /**
     * Loads PCK options for the selected GCK value.
     * Sets loading state for both PCK and price group fields during the request.
     * @param gckValue - The selected GCK value
     * @returns Observable that emits the available PCK options
     */
    private loadPckOptionsForGck(gckValue: string): Observable<string[]> {
        this.workflowOptionSetService.setFieldLoading('pck-search');
        this.workflowOptionSetService.setFieldLoading('price-group-search');
        return this.workflowOptionSetService.getWorkflowFieldOptionSet(new Date(), 'pck', 'gck', gckValue).pipe(
            finalize(() => {
                this.workflowOptionSetService.clearFieldLoading('pck-search');
                this.workflowOptionSetService.clearFieldLoading('price-group-search');
            })
        );
    }

    /**
     * Updates the PCK field configuration with new options.
     * Stores options in the WorkflowOptionSetService for dynamic retrieval.
     * @param options - The array of available PCK options
     */
    private updateSnowflakePckOptions(options: string[]): void {
        this.workflowOptionSetService.setFieldOptions('pck-search', options);
    }

    /**
     * Sets up a listener for PCK changes.
     * When PCK changes, fetches available price group options for all selected PCKs and resets the price group field.
     * Supports both single and multiple PCK selection (multi-select).
     */
    private setupSnowflakePCKListener(): void {
        const form = this.stepForm();

        const gckControl = form.get('gck-search') as FormControl;
        const pckControl = form.get('pck-search') as FormControl;
        const priceGroup = form.get('price-group-search') as FormControl;

        if (!gckControl || !pckControl) return;

        pckControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                switchMap(value => {
                    // Handle empty or null values
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        priceGroup.reset();
                        this.updateSnowflakePricegroupValue([]);
                        return of([] as string[]);
                    }

                    // Convert single value to array for consistent handling
                    const pckValues = Array.isArray(value) ? value : [value];
                    const gckValue = gckControl.value.toString();

                    // Load price groups for all selected PCKs in parallel
                    const priceGroupRequests = pckValues.map(pck =>
                        this.loadPriceGroupOptionsForPck(gckValue, pck.toString())
                    );

                    // If no requests, return empty array
                    if (priceGroupRequests.length === 0) {
                        return of([] as string[]);
                    }

                    // Combine all results and extract unique values
                    return forkJoin(priceGroupRequests).pipe(
                        map(results => {
                            const allPriceGroups = results.flat();
                            return [...new Set(allPriceGroups)];
                        })
                    );
                }),
                tap(options => {
                    this.updateSnowflakePricegroupValue(options ?? []);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    /**
     * Loads price group options for the selected GCK and PCK combination.
     * Sets loading state for the price group field during the request.
     * @param gckValue - The selected GCK value
     * @param pckValue - The selected PCK value
     * @returns Observable that emits the available price group options
     */
    private loadPriceGroupOptionsForPck(gckValue: string, pckValue: string): Observable<string[]> {
        this.workflowOptionSetService.setFieldLoading('price-group-search');
        return this.workflowOptionSetService
            .getWorkflowFieldOptionSet(new Date(), 'pricegroup', 'gck', gckValue, 'pck', pckValue)
            .pipe(finalize(() => this.workflowOptionSetService.clearFieldLoading('price-group-search')));
    }

    /**
     * Updates the price group field configuration with new options.
     * Stores options in the WorkflowOptionSetService for dynamic retrieval.
     * Always adds an option for empty/unmaintained fields at the beginning.
     * @param options - The array of available price group options
     */
    private updateSnowflakePricegroupValue(options: string[]): void {
        // Add empty/unmaintained option with translation key
        const emptyOptionLabel = this.translateService.instant('workflow.snowflake.empty-price-group');
        const optionsWithEmpty = [emptyOptionLabel, ...options];

        this.workflowOptionSetService.setFieldOptions('price-group-search', optionsWithEmpty);
    }

    /**
     * Sets up a listener for the core MLFB (Material Number) field changes.
     * Updates a signal to track whether a core MLFB value is set, which controls UI state.
     */
    private setupCoreMlfbListener(): void {
        const form = this.stepForm();
        const coreMlfbControl = form.get('search-core-mlfb') as FormControl;

        if (!coreMlfbControl) return;

        coreMlfbControl.valueChanges
            .pipe(
                startWith(coreMlfbControl.value),
                tap(value => this.isCoreMlfbSet.set(!!value)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }
}
